import { utcToZonedTime } from 'date-fns-tz';
import decode from 'jwt-decode';
import cloneDeep from 'lodash.clonedeep';
import get from 'lodash.get';
import mergeWith from 'lodash.mergewith';
import moment from 'moment';
import momentTimezone from 'moment-timezone';
import { IconBaseProps } from 'react-icons/lib';
import { defaultTheme } from 'react-select/lib/theme';
import EmailPrivateIcon from 'src/components/Icon/EmailPrivateIcon';
import EmailPublicIcon from 'src/components/Icon/EmailPublicIcon';

export const getAuthToken = () => localStorage.getItem('AUTH_TOKEN');
export const setAuthToken = (token: string) => localStorage.setItem('AUTH_TOKEN', token);
export const removeAuthToken = () => localStorage.removeItem('AUTH_TOKEN');

export const getRefreshToken = () => localStorage.getItem('REFRESH_TOKEN');
export const setRefreshToken = (token: string) => localStorage.setItem('REFRESH_TOKEN', token);

export const getCurrentUserID = () => localStorage.getItem('USER_ID');
export const setCurrentUserInfo = () => {
  const userInfo = decode<AuthToken>(getAuthToken() || '');
  localStorage.setItem('USER_ID', userInfo.user_id);
  localStorage.setItem('EXPIRY', userInfo.exp.toString());
};

export const getCurrentBusinessID = () => localStorage.getItem('BUSINESS_ID');
export const setCurrentBusinessID = (id: string) => localStorage.setItem('BUSINESS_ID', id);
export const removeCurrentBusinessID = () => localStorage.removeItem('BUSINESS_ID');

export const getCommunityID = () => localStorage.getItem('SOURCE_ENTITY_ID');
export const setCommunityID = (id: string) => localStorage.setItem('SOURCE_ENTITY_ID', id);
export const removeCommunityID = () => localStorage.removeItem('SOURCE_ENTITY_ID');

export const getParentEntityID = () => localStorage.getItem('PARENT_ENTITY_ID');
export const setParentEntityID = (id: string) => localStorage.setItem('PARENT_ENTITY_ID', id);
export const removeParentEntityID = () => localStorage.removeItem('PARENT_ENTITY_ID');

export const getTargetEntityID = () => localStorage.getItem('TARGET_ENTITY_ID');
export const setTargetEntityID = (id: string) => localStorage.setItem('TARGET_ENTITY_ID', id);
export const removeTargetEntityID = () => localStorage.removeItem('TARGET_ENTITY_ID');

export const getTokenExpiry = () => localStorage.getItem('EXPIRY');

export const getSocketID = () => localStorage.getItem('SOCKET_ID');
export const setSocketID = (id: string) => localStorage.setItem('SOCKET_ID', id);

export const getFocusedBusinessID = () => localStorage.getItem('FOCUSED_ID');
export const setFocusedBusinessID = (id: string) => localStorage.setItem('FOCUSED_ID', id);
export const removeFocusedBusinessID = () => localStorage.removeItem('FOCUSED_ID');

export const isAuthenticated = (): boolean => {
  const currentTime = Date.now() / 1000;
  if (getAuthToken() && parseInt(getTokenExpiry() || '', 10) > currentTime) {
    return true;
  }

  return false;
};

export const logout = () => {
  localStorage.clear();
  location.reload();
};

export const login = (authToken: string, refreshToken: string) => {
  setAuthToken(authToken);
  setRefreshToken(refreshToken);
  setCurrentUserInfo();
};

export const closePopover = () => document.body.click();

export const defaultPaginationObject = <T>(): PaginatedResponse<T> => {
  return {
    count: 0,
    previous: null,
    next: null,
    results: [],
  };
};

export const updatePaginationData = <T>(
  newObj: PaginatedResponse<T>,
  oldObj: PaginatedResponse<T>
): PaginatedResponse<T> => {
  return {
    ...newObj,
    results: newObj.previous ? [...oldObj.results, ...newObj.results] : newObj.results,
  };
};

export const pushPaginationItem = <T>(newObj: T, oldObj: PaginatedResponse<T>): PaginatedResponse<T> => {
  return {
    ...oldObj,
    count: oldObj.count + 1,
    results: [...oldObj.results, newObj],
  };
};

export const generateNumberedDropdownOptions = (maxNumber: number, includeZero?: boolean) =>
  [...Array(maxNumber + (includeZero ? 1 : 0))].map((_emptyElement, index) => ({
    label: `${index + (includeZero ? 0 : 1)}`,
    value: index + (includeZero ? 0 : 1),
  }));

export const reactSelectTheme = {
  ...defaultTheme,
  colors: {
    ...defaultTheme.colors,
    primary75: 'rgba(5, 16, 111, 0.75)',
    primary50: 'rgba(5, 16, 111, 0.50)',
    primary25: 'rgba(5, 16, 111, 0.25)',
    primary: 'rgba(5, 16, 111, 1)',
  },
};

export const formatSelectOptions = <T>(arr: T[] = [], label: string, value: string): Array<SelectProps<T>> =>
  arr.map((item: T): SelectProps<T> => ({ ...item, label: get(item, label, item), value: get(item, value, item) }));

export const getDateStringFromFormat = (
  dateString: string,
  timeZone: string,
  format: string = 'MM/DD/YYYY hh:mm A'
) => {
  return momentTimezone(dateString)
    .tz(timeZone)
    .format(format);
};

export const emailAccountScopeMap: { [key: string]: React.FC<IconBaseProps> } = {
  public: EmailPublicIcon,
  private: EmailPrivateIcon,
};

export const timeStringToDate = (timeString: string, timeZone?: string) => {
  const dateObject = new Date();
  const timeArray = timeString.split(':');

  const hours = parseInt(timeArray[0], 0);
  const minutes = parseInt(timeArray[1], 0);

  dateObject.setUTCHours(hours, minutes, 0, 0);

  return utcToZonedTime(dateObject, timeZone);
};

export const dateToTimeString = (dateInput: Date, timeZone: string) => {
  const momentString = moment(dateInput).format('YYYY-MM-DD HH:mm:ss.SSS');
  const timezoneSpecificMoment = momentTimezone.tz(momentString, timeZone);

  return timezoneSpecificMoment.utc().format('HH:mm');
};

export const dateObjToStartOfDayString = (date: Date) =>
  moment(date)
    .format('YYYY-MM-DD')
    .concat('T00:00:00.000Z');

export const utcStringToDate = (utcString: string) => {
  const momentUtcObject = moment.utc(utcString);

  return new Date(momentUtcObject.year(), momentUtcObject.month(), momentUtcObject.date());
};

export const getUTCString = (dateInput: Date) => {
  const momentObject = moment.utc(dateInput);
  const updatedDateObject = new Date();
  updatedDateObject.setUTCDate(momentObject.date());
  updatedDateObject.setUTCMonth(momentObject.month());
  updatedDateObject.setUTCFullYear(momentObject.year());
  updatedDateObject.setUTCHours(0, 0, 0, 0);

  return updatedDateObject.toISOString();
};

export const getTimezoneSpecificISOString = (browserTimezoneDate: Date, timeZone: string) => {
  const momentString = moment(browserTimezoneDate).format('YYYY-MM-DD HH:mm:ss.SSS');
  const timezoneSpecificUTC = momentTimezone.tz(momentString, timeZone).toISOString();

  return timezoneSpecificUTC;
};

export const update: Update = (object, newObject, modifier) =>
  mergeWith(cloneDeep(object), cloneDeep(newObject), modifier);

export const highlightKeywords = (keywords: string[], text: string) => {
  let result = text;
  keywords.forEach(keyword => {
    const keywordRegex = new RegExp(escapeRegexString(keyword), 'gi');
    result = result.replace(keywordRegex, `<mark>${keyword}</mark>`);
  });

  return result;
};

const customUnescape = (encodedString: string) => {
  const parser = new DOMParser();
  const dom = parser.parseFromString('<!doctype html><body>' + encodedString, 'text/html');
  const decodedString = dom.body.textContent;

  return decodedString;
};

const escapeRegexString = (inputString: string) => {
  return inputString.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'); // $& means the whole matched string
};

export const highlightKeywordsForMarkup = (keywords: string[], text: string) => {
  const tagIndices: Array<{ startIndex: number; endIndex: number }> = [];
  text.replace(/<(.|n)*?>/gim, (match, _p1, subIndex) => {
    const indiceObject = {
      startIndex: subIndex,
      endIndex: subIndex + match.length - 1,
    };
    tagIndices.push(indiceObject);

    return match;
  });
  if (!tagIndices.length) {
    return highlightKeywords(keywords, text);
  }
  let lastIteratedIndex = 0;
  const modifiedOnlyTextArray: string[] = [];
  const textMappedIndices: number[] = [];
  const modifiedUnescapedTextArray: Array<{ type: number; character: string; marked: boolean }> = [];
  let overallIndex = 0;
  tagIndices.forEach(tagIndexObj => {
    const currentWord = text.substring(lastIteratedIndex, tagIndexObj.startIndex);
    if (currentWord) {
      let previouslyPushedElement = '';
      customUnescape(currentWord)
        .split('')
        .forEach(currentWordChar => {
          if (['\n', '\t', '\b', '\r', ' ', '\xa0'].includes(currentWordChar)) {
            if (previouslyPushedElement !== ' ') {
              modifiedOnlyTextArray.push(' ');
              modifiedUnescapedTextArray.push({ character: ' ', type: 0, marked: false });
              textMappedIndices.push(overallIndex);
              overallIndex++;
            }
            previouslyPushedElement = ' ';
          } else {
            modifiedOnlyTextArray.push(currentWordChar);
            modifiedUnescapedTextArray.push({ character: currentWordChar, type: 0, marked: false });
            textMappedIndices.push(overallIndex);
            previouslyPushedElement = currentWordChar;
            overallIndex++;
          }
        });
    }
    const currentTagArray = text.substring(tagIndexObj.startIndex, tagIndexObj.endIndex + 1);
    overallIndex += currentTagArray.length;
    currentTagArray.split('').forEach(tagCharacter => {
      modifiedUnescapedTextArray.push({ type: 1, character: tagCharacter, marked: false });
    });
    lastIteratedIndex = tagIndexObj.endIndex + 1;
  });
  const modifiedOnlyText = modifiedOnlyTextArray.join('');
  let highlightedText = '';
  const patternPositions: Array<{ startIndex: number; endIndex: number }> = [];
  keywords.forEach(keyword => {
    const keywordRegexp = new RegExp(escapeRegexString(keyword), 'gi');
    modifiedOnlyText.replace(keywordRegexp, (match, subIndex) => {
      patternPositions.push({
        startIndex: subIndex,
        endIndex: subIndex + match.length - 1,
      });

      return match;
    });
  });
  overallIndex = 0;
  patternPositions
    .sort((a, b) => a.startIndex - b.startIndex)
    .forEach(patternPosition => {
      const { endIndex, startIndex } = patternPosition;
      const currentStart = textMappedIndices[startIndex];
      const currentEnd = textMappedIndices[endIndex];
      for (let i = overallIndex; i < currentStart; i++) {
        if (!modifiedUnescapedTextArray[i].marked) {
          highlightedText += `${modifiedUnescapedTextArray[i].character}`;
        }
      }
      let customHighlighted = ``;
      let startMarker = false;
      for (let i = currentStart; i < currentEnd + 1; i++) {
        if (!modifiedUnescapedTextArray[i].type && !modifiedUnescapedTextArray[i].marked) {
          if (startMarker) {
            customHighlighted += `${modifiedUnescapedTextArray[i].character}`;
            modifiedUnescapedTextArray[i].marked = true;
          } else {
            customHighlighted += `<mark>${modifiedUnescapedTextArray[i].character}`;
            modifiedUnescapedTextArray[i].marked = true;
            startMarker = true;
          }
        } else if (!modifiedUnescapedTextArray[i].marked) {
          if (startMarker) {
            customHighlighted += `</mark>`;
          }
          customHighlighted += `${modifiedUnescapedTextArray[i].character}`;
          modifiedUnescapedTextArray[i].marked = true;
          startMarker = false;
        }
      }
      if (startMarker) {
        customHighlighted += `</mark>`;
      }
      highlightedText += customHighlighted;
      overallIndex = currentEnd + 1;
    });
  for (let i = overallIndex; i < modifiedUnescapedTextArray.length; i++) {
    if (!modifiedUnescapedTextArray[i].marked) {
      highlightedText += `${modifiedUnescapedTextArray[i].character}`;
    }
  }

  return highlightedText;
};

export const highlightMentions = (text: string) => {
  let result = text;
  const regex = /\[\[([^\[]*[^\]])\]\]/gim;
  const values = text.match(regex);
  const tokens = text.split(regex);
  tokens.forEach((subText: string) => {
    if ((values || []).includes(`[[${subText}]]`)) {
      const splittedText = subText.split(':');
      const isCKEFeed = result.includes('<span id="'); // TODO: Think of a better way to figure out if a CKE feed or not
      result = result.replace(
        `[[${subText}]]`,
        isCKEFeed ? '' : `<strong class="text-primary">${splittedText[0]}</strong>`
      );
    }
  });

  return result;
};

export const capitalizeText = (inputText: string) => {
  return inputText
    .split(' ')
    .map(text => `${text[0].toUpperCase()}${text.slice(1, text.length)}`)
    .join(' ');
};

export const reorderListForDnd: <T>(items: T[], sourceIndex: number, destinationIndex: number) => T[] = (
  items,
  sourceIndex,
  destinationIndex
) => {
  const result = Array.from(items);
  const [removed] = result.splice(sourceIndex, 1);
  result.splice(destinationIndex, 0, removed);

  return result;
};

export const getTargetBusinessUuid = (sourceUuid: string, targetUuid: string) =>
  sourceUuid === getCurrentBusinessID() ? targetUuid : sourceUuid;

export const isPointInsidePolygon = (point: number[], vs: number[][]) => {
  // ray-casting algorithm based on
  // https://wrf.ecse.rpi.edu/Research/Short_Notes/pnpoly.html/pnpoly.html

  const x = point[0];
  const y = point[1];

  let inside = false;
  //tslint:disable
  for (let i = 0, j = vs.length - 1; i < vs.length; j = i++) {
    var xi = vs[i][0],
      yi = vs[i][1];
    var xj = vs[j][0],
      yj = vs[j][1];

    var intersect = yi > y != yj > y && x < ((xj - xi) * (y - yi)) / (yj - yi) + xi;
    if (intersect) inside = !inside;
  }

  return inside;
};

export const getTextFromHtmlString = (html: string) => {
  if (html) {
    const div = document.createElement('span');
    div.style.display = 'none';
    div.innerHTML = html;
    const text = div.textContent || div.innerText || '';
    div.remove();

    return text;
  }
};

export const getTimezones = () => [
  'Africa/Abidjan',
  'Africa/Accra',
  'Africa/Algiers',
  'Africa/Bissau',
  'Africa/Cairo',
  'Africa/Casablanca',
  'Africa/Ceuta',
  'Africa/El_Aaiun',
  'Africa/Johannesburg',
  'Africa/Juba',
  'Africa/Khartoum',
  'Africa/Lagos',
  'Africa/Maputo',
  'Africa/Monrovia',
  'Africa/Nairobi',
  'Africa/Ndjamena',
  'Africa/Sao_Tome',
  'Africa/Tripoli',
  'Africa/Tunis',
  'Africa/Windhoek',
  'America/Adak',
  'America/Anchorage',
  'America/Araguaina',
  'America/Argentina/Buenos_Aires',
  'America/Argentina/Catamarca',
  'America/Argentina/Cordoba',
  'America/Argentina/Jujuy',
  'America/Argentina/La_Rioja',
  'America/Argentina/Mendoza',
  'America/Argentina/Rio_Gallegos',
  'America/Argentina/Salta',
  'America/Argentina/San_Juan',
  'America/Argentina/San_Luis',
  'America/Argentina/Tucuman',
  'America/Argentina/Ushuaia',
  'America/Asuncion',
  'America/Atikokan',
  'America/Bahia',
  'America/Bahia_Banderas',
  'America/Barbados',
  'America/Belem',
  'America/Belize',
  'America/Blanc-Sablon',
  'America/Boa_Vista',
  'America/Bogota',
  'America/Boise',
  'America/Cambridge_Bay',
  'America/Campo_Grande',
  'America/Cancun',
  'America/Caracas',
  'America/Cayenne',
  'America/Chicago',
  'America/Chihuahua',
  'America/Costa_Rica',
  'America/Creston',
  'America/Cuiaba',
  'America/Curacao',
  'America/Danmarkshavn',
  'America/Dawson',
  'America/Dawson_Creek',
  'America/Denver',
  'America/Detroit',
  'America/Edmonton',
  'America/Eirunepe',
  'America/El_Salvador',
  'America/Fort_Nelson',
  'America/Fortaleza',
  'America/Glace_Bay',
  'America/Godthab',
  'America/Goose_Bay',
  'America/Grand_Turk',
  'America/Guatemala',
  'America/Guayaquil',
  'America/Guyana',
  'America/Halifax',
  'America/Havana',
  'America/Hermosillo',
  'America/Indiana/Indianapolis',
  'America/Indiana/Knox',
  'America/Indiana/Marengo',
  'America/Indiana/Petersburg',
  'America/Indiana/Tell_City',
  'America/Indiana/Vevay',
  'America/Indiana/Vincennes',
  'America/Indiana/Winamac',
  'America/Inuvik',
  'America/Iqaluit',
  'America/Jamaica',
  'America/Juneau',
  'America/Kentucky/Louisville',
  'America/Kentucky/Monticello',
  'America/La_Paz',
  'America/Lima',
  'America/Los_Angeles',
  'America/Maceio',
  'America/Managua',
  'America/Manaus',
  'America/Martinique',
  'America/Matamoros',
  'America/Mazatlan',
  'America/Menominee',
  'America/Merida',
  'America/Metlakatla',
  'America/Mexico_City',
  'America/Miquelon',
  'America/Moncton',
  'America/Monterrey',
  'America/Montevideo',
  'America/Nassau',
  'America/New_York',
  'America/Nipigon',
  'America/Nome',
  'America/Noronha',
  'America/North_Dakota/Beulah',
  'America/North_Dakota/Center',
  'America/North_Dakota/New_Salem',
  'America/Ojinaga',
  'America/Panama',
  'America/Pangnirtung',
  'America/Paramaribo',
  'America/Phoenix',
  'America/Port-au-Prince',
  'America/Port_of_Spain',
  'America/Porto_Velho',
  'America/Puerto_Rico',
  'America/Punta_Arenas',
  'America/Rainy_River',
  'America/Rankin_Inlet',
  'America/Recife',
  'America/Regina',
  'America/Resolute',
  'America/Rio_Branco',
  'America/Santarem',
  'America/Santiago',
  'America/Santo_Domingo',
  'America/Sao_Paulo',
  'America/Scoresbysund',
  'America/Sitka',
  'America/St_Johns',
  'America/Swift_Current',
  'America/Tegucigalpa',
  'America/Thule',
  'America/Thunder_Bay',
  'America/Tijuana',
  'America/Toronto',
  'America/Vancouver',
  'America/Whitehorse',
  'America/Winnipeg',
  'America/Yakutat',
  'America/Yellowknife',
  'Antarctica/Casey',
  'Antarctica/Davis',
  'Antarctica/DumontDUrville',
  'Antarctica/Macquarie',
  'Antarctica/Mawson',
  'Antarctica/Palmer',
  'Antarctica/Rothera',
  'Antarctica/Syowa',
  'Antarctica/Troll',
  'Antarctica/Vostok',
  'Asia/Almaty',
  'Asia/Amman',
  'Asia/Anadyr',
  'Asia/Aqtau',
  'Asia/Aqtobe',
  'Asia/Ashgabat',
  'Asia/Atyrau',
  'Asia/Baghdad',
  'Asia/Baku',
  'Asia/Bangkok',
  'Asia/Barnaul',
  'Asia/Beirut',
  'Asia/Bishkek',
  'Asia/Brunei',
  'Asia/Calcutta',
  'Asia/Chita',
  'Asia/Choibalsan',
  'Asia/Colombo',
  'Asia/Damascus',
  'Asia/Dhaka',
  'Asia/Dili',
  'Asia/Dubai',
  'Asia/Dushanbe',
  'Asia/Famagusta',
  'Asia/Gaza',
  'Asia/Hebron',
  'Asia/Ho_Chi_Minh',
  'Asia/Hong_Kong',
  'Asia/Hovd',
  'Asia/Irkutsk',
  'Asia/Jakarta',
  'Asia/Jayapura',
  'Asia/Jerusalem',
  'Asia/Kabul',
  'Asia/Kamchatka',
  'Asia/Karachi',
  'Asia/Kathmandu',
  'Asia/Khandyga',
  'Asia/Krasnoyarsk',
  'Asia/Kuala_Lumpur',
  'Asia/Kuching',
  'Asia/Macau',
  'Asia/Magadan',
  'Asia/Makassar',
  'Asia/Manila',
  'Asia/Nicosia',
  'Asia/Novokuznetsk',
  'Asia/Novosibirsk',
  'Asia/Omsk',
  'Asia/Oral',
  'Asia/Pontianak',
  'Asia/Pyongyang',
  'Asia/Qatar',
  'Asia/Qostanay',
  'Asia/Qyzylorda',
  'Asia/Riyadh',
  'Asia/Sakhalin',
  'Asia/Samarkand',
  'Asia/Seoul',
  'Asia/Shanghai',
  'Asia/Singapore',
  'Asia/Srednekolymsk',
  'Asia/Taipei',
  'Asia/Tashkent',
  'Asia/Tbilisi',
  'Asia/Tehran',
  'Asia/Thimphu',
  'Asia/Tokyo',
  'Asia/Tomsk',
  'Asia/Ulaanbaatar',
  'Asia/Urumqi',
  'Asia/Ust-Nera',
  'Asia/Vladivostok',
  'Asia/Yakutsk',
  'Asia/Yangon',
  'Asia/Yekaterinburg',
  'Asia/Yerevan',
  'Atlantic/Azores',
  'Atlantic/Bermuda',
  'Atlantic/Canary',
  'Atlantic/Cape_Verde',
  'Atlantic/Faroe',
  'Atlantic/Madeira',
  'Atlantic/Reykjavik',
  'Atlantic/South_Georgia',
  'Atlantic/Stanley',
  'Australia/Adelaide',
  'Australia/Brisbane',
  'Australia/Broken_Hill',
  'Australia/Currie',
  'Australia/Darwin',
  'Australia/Eucla',
  'Australia/Hobart',
  'Australia/Lindeman',
  'Australia/Lord_Howe',
  'Australia/Melbourne',
  'Australia/Perth',
  'Australia/Sydney',
  'Europe/Amsterdam',
  'Europe/Andorra',
  'Europe/Astrakhan',
  'Europe/Athens',
  'Europe/Belgrade',
  'Europe/Berlin',
  'Europe/Brussels',
  'Europe/Bucharest',
  'Europe/Budapest',
  'Europe/Chisinau',
  'Europe/Copenhagen',
  'Europe/Dublin',
  'Europe/Gibraltar',
  'Europe/Helsinki',
  'Europe/Istanbul',
  'Europe/Kaliningrad',
  'Europe/Kiev',
  'Europe/Kirov',
  'Europe/Lisbon',
  'Europe/London',
  'Europe/Luxembourg',
  'Europe/Madrid',
  'Europe/Malta',
  'Europe/Minsk',
  'Europe/Monaco',
  'Europe/Moscow',
  'Europe/Oslo',
  'Europe/Paris',
  'Europe/Prague',
  'Europe/Riga',
  'Europe/Rome',
  'Europe/Samara',
  'Europe/Saratov',
  'Europe/Simferopol',
  'Europe/Sofia',
  'Europe/Stockholm',
  'Europe/Tallinn',
  'Europe/Tirane',
  'Europe/Ulyanovsk',
  'Europe/Uzhgorod',
  'Europe/Vienna',
  'Europe/Vilnius',
  'Europe/Volgograd',
  'Europe/Warsaw',
  'Europe/Zaporozhye',
  'Europe/Zurich',
  'Indian/Chagos',
  'Indian/Christmas',
  'Indian/Cocos',
  'Indian/Kerguelen',
  'Indian/Mahe',
  'Indian/Maldives',
  'Indian/Mauritius',
  'Indian/Reunion',
  'Pacific/Apia',
  'Pacific/Auckland',
  'Pacific/Bougainville',
  'Pacific/Chatham',
  'Pacific/Chuuk',
  'Pacific/Easter',
  'Pacific/Efate',
  'Pacific/Enderbury',
  'Pacific/Fakaofo',
  'Pacific/Fiji',
  'Pacific/Funafuti',
  'Pacific/Galapagos',
  'Pacific/Gambier',
  'Pacific/Guadalcanal',
  'Pacific/Guam',
  'Pacific/Honolulu',
  'Pacific/Kiritimati',
  'Pacific/Kosrae',
  'Pacific/Kwajalein',
  'Pacific/Majuro',
  'Pacific/Marquesas',
  'Pacific/Nauru',
  'Pacific/Niue',
  'Pacific/Norfolk',
  'Pacific/Noumea',
  'Pacific/Pago_Pago',
  'Pacific/Palau',
  'Pacific/Pitcairn',
  'Pacific/Pohnpei',
  'Pacific/Port_Moresby',
  'Pacific/Rarotonga',
  'Pacific/Tahiti',
  'Pacific/Tarawa',
  'Pacific/Tongatapu',
  'Pacific/Wake',
  'Pacific/Wallis',
];
